import React, { useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, Terminal as TerminalIcon } from "lucide-react";
import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";

import type { SilaeFormData } from "@/types/silae";
import { Terminal, TypingAnimation } from "@/components/magicui/terminal";

interface Step6GenerationProps {
  formik: FormikProps<SilaeFormData>;
  generatedCode: string;
  isGenerating: boolean;
  onGenerate: () => void;
  onCopy: () => void;
  onDownload: () => void;
  onEdit?: (editedCode: string) => void;
}

/**
 * Étape 6 : Génération du code SILAE
 * Étape finale dédiée à la génération et export du code avec animation
 */
export const Step6Generation: React.FC<Step6GenerationProps> = ({
  formik,
  generatedCode,
  isGenerating,
  onGenerate,
  onCopy,
  onDownload,
  onEdit,
}) => {
  const { t } = useTranslation();
  const [hasAutoGenerated, setHasAutoGenerated] = React.useState(false);

  // Hash stable basé sur les données réelles du formulaire actuel
  const configHash = useMemo(() => {
    return JSON.stringify({
      structureType: formik.values.structureType,
      ifBonusCount: formik.values.ifBranch?.bonuses?.length ?? 0,
      elseBonusCount: formik.values.elseBranch?.bonuses?.length ?? 0,
      conditionsCount: formik.values.conditions?.length ?? 0,
      hasExclusions: formik.values.hasExclusions,
      exclusionsCount: formik.values.exclusions?.length ?? 0,
    });
  }, [
    formik.values.structureType,
    formik.values.ifBranch?.bonuses?.length,
    formik.values.elseBranch?.bonuses?.length,
    formik.values.conditions?.length,
    formik.values.hasExclusions,
    formik.values.exclusions?.length,
  ]);

  // Réinitialiser l'auto-génération quand la configuration change
  useEffect(() => {
    setHasAutoGenerated(false);
  }, [configHash]);

  // Génération automatique avec debounce 500ms (limite les appels API)
  useEffect(() => {
    if (!hasAutoGenerated && !isGenerating) {
      const timer = setTimeout(() => {
        setHasAutoGenerated(true);
        onGenerate();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hasAutoGenerated, isGenerating, onGenerate]);

  // Mémoriser les handlers pour éviter les re-renders
  const memoizedHandleCopy = useCallback(async () => {
    await onCopy();
  }, [onCopy]);

  const memoizedHandleDownload = useCallback(() => {
    onDownload();
  }, [onDownload]);

  // Composant Terminal mémorisé pour éviter les re-renders
  const MemoizedTerminal = useMemo(() => {
    if (!hasAutoGenerated || isGenerating || !generatedCode) {
      return (
        <Terminal
          className="w-full self-center max-w-none lg:w-[800px] min-h-[420px] sm:min-h-[420px] lg:min-h-[420px] bg-gray-800 dark:bg-gray-950 text-xs sm:text-sm lg:text-base overflow-hidden"
          sequence={false}
          onCopy={undefined}
          onDownload={undefined}
        >
          <div className="text-gray-400 p-4 flex items-center gap-2">
            {isGenerating && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("step6Generation.generating")}
          </div>
        </Terminal>
      );
    }

    return (
      <Terminal
        className="w-full self-center max-w-none lg:w-[800px] min-h-[420px] sm:min-h-[420px] lg:min-h-[420px] bg-gray-800 dark:bg-gray-950 text-xs sm:text-sm lg:text-base overflow-hidden"
        sequence={false}
        onCopy={memoizedHandleCopy}
        onDownload={memoizedHandleDownload}
        onEdit={onEdit}
        editableCode={generatedCode}
      >
        <TypingAnimation
          duration={10}
          delay={200}
          startOnView={false}
          className="text-lime-400 font-mono"
          key={`terminal-${configHash}`}
        >
          {generatedCode}
        </TypingAnimation>
      </Terminal>
    );
  }, [
    hasAutoGenerated,
    isGenerating,
    generatedCode,
    configHash,
    memoizedHandleCopy,
    memoizedHandleDownload,
    onEdit,
    t,
  ]);

  return (
    <div className="space-y-6 p-0">
      {/* Section de génération du code */}
      <Card className="">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TerminalIcon className="h-5 w-5" />
            {t("step6Generation.title")}
          </CardTitle>
          <CardDescription>{t("step6Generation.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Code généré avec Terminal animé */}
          {generatedCode && (
            <div className="space-y-4 flex flex-col items-stretch justify-center">
              {MemoizedTerminal}
            </div>
          )}

          {/* Instructions d'utilisation */}
          {generatedCode && (
            <Card className="bg-blue-50 gap-0 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-blue-900 dark:text-blue-100">
                  {t("step6Generation.instructions.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                  <p>{t("step6Generation.instructions.step1")}</p>
                  <p>{t("step6Generation.instructions.step2")}</p>
                  <p>{t("step6Generation.instructions.step3")}</p>
                  <p>{t("step6Generation.instructions.step4")}</p>
                  <p>{t("step6Generation.instructions.step5")}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
